import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, MapPin } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { fileUrl } from "@/lib/api";
import SearchBox from "@/components/site/SearchBox";
import {
  Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";

const MENU_LINKS = [
  { label: "Início", to: "/", key: null },
  { label: "Empresas", to: "/empresas", key: "all_businesses" },
  { label: "Serviços", to: "/servicos", key: "services" },
  { label: "Reels", to: "/reels", key: "reels", menuFlag: true },
  { label: "Promoções", to: "/#promotions", key: "promotions" },
  { label: "Cupons", to: "/#coupons", key: "coupons" },
  { label: "Notícias", to: "/#news", key: "news" },
  { label: "Eventos", to: "/#events", key: "events" },
  { label: "Pontos Turísticos", to: "/#tourist_spots", key: "tourist_spots" },
  { label: "Vagas de Emprego", to: "/#jobs", key: "jobs" },
  { label: "Casas para Alugar", to: "/#rentals", key: "rentals" },
  { label: "Pontos Comerciais", to: "/#commercial_spots", key: "commercial_spots" },
];

const Header = () => {
  const { settings, sections, isEnabled } = useSite();
  const [open, setOpen] = useState(false);

  const reelsMenu = sections.find((s) => s.key === "reels")?.menu_enabled !== false;
  const links = MENU_LINKS.filter((l) => (l.menuFlag ? reelsMenu : !l.key || isEnabled(l.key)));

  return (
    <header className="sticky top-0 z-40 bg-card/95 backdrop-blur border-b border-border" data-testid="site-header">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex items-center gap-3 h-16">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="header-logo">
            {settings?.logo_url ? (
              <img src={fileUrl(settings.logo_url)} alt="logo" className="h-9 w-9 rounded-xl object-cover" />
            ) : (
              <div className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center text-primary-foreground font-bold font-heading">
                DP
              </div>
            )}
            <div className="leading-tight hidden xs:block sm:block">
              <div className="font-bold text-foreground font-heading text-sm sm:text-base">
                {settings?.site_name || "Divulga Pocinhos Guia"}
              </div>
              <div className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                <MapPin className="w-3 h-3" /> Pocinhos-PB
              </div>
            </div>
          </Link>

          <SearchBox
            className="flex-1 max-w-md ml-auto hidden md:block"
            placeholder={settings?.search_placeholder || "O que você procura?"}
            inputTestId="header-search-input"
          />

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="menu-button"
                className="ml-auto md:ml-0 p-2 rounded-full hover:bg-muted transition-colors"
                aria-label="Menu"
              >
                <Menu className="w-6 h-6 text-foreground" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80">
              <SheetHeader>
                <SheetTitle className="font-heading">{settings?.site_name || "Menu"}</SheetTitle>
              </SheetHeader>
              <nav className="mt-6 flex flex-col gap-1">
                {links.map((l) => (
                  <Link
                    key={l.label}
                    to={l.to}
                    onClick={() => setOpen(false)}
                    data-testid={`menu-link-${l.label}`}
                    className="px-3 py-3 rounded-xl text-foreground hover:bg-muted font-medium transition-colors"
                  >
                    {l.label}
                  </Link>
                ))}
              </nav>
            </SheetContent>
          </Sheet>
        </div>

        <div className="md:hidden pb-3">
          <SearchBox
            placeholder={settings?.search_placeholder || "O que você procura?"}
            inputTestId="header-search-input-mobile"
          />
        </div>
      </div>
    </header>
  );
};

export default Header;
